import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import { cn } from '~/lib/utils'

interface UserBadgeProps {
  name: string
  avatarUrl?: string | null
  size?: 'sm' | 'default' | 'lg'
  /** Hide the name, rendering only the avatar (e.g. in dense rows). */
  avatarOnly?: boolean
  className?: string
}

/**
 * Standard "round image + name" rendering for a user. Use everywhere a user is
 * referenced in the UI — keeps sizing + fallback behavior consistent.
 */
export function UserBadge({
  name,
  avatarUrl,
  size = 'default',
  avatarOnly = false,
  className,
}: Readonly<UserBadgeProps>) {
  const initials = toInitials(name)
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <Avatar size={size}>
        {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>
      {!avatarOnly && <span className="text-sm">{name}</span>}
    </span>
  )
}

function toInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  const first = parts[0]
  const last = parts.at(-1)
  if (!first || !last) return '?'
  if (parts.length === 1) return first.charAt(0).toUpperCase()
  return (first.charAt(0) + last.charAt(0)).toUpperCase()
}
